// mvp-returns Phase G5 — Return prevention alert client. Endpoints
// live on de-order-service: /ord/return-prevention-alerts/*.

import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

export type AlertSeverity = "INFO" | "WARNING" | "CRITICAL";
export type AlertStatus = "ACTIVE" | "ACKNOWLEDGED" | "MUTED" | "RESOLVED";
export type AlertTrigger =
  | "RETURN_RATE_THRESHOLD"
  | "REASON_CONCENTRATION"
  | "SUDDEN_SPIKE"
  | "REPEAT_DEFECT";

export interface IAlertSummary {
  id: string;
  productId: string | null;
  productName: string | null;
  sku: string | null;
  supplierBusinessId: string;
  triggerType: AlertTrigger;
  severity: AlertSeverity;
  title: string;
  message: string;
  windowType: string | null;
  status: AlertStatus;
  acknowledgedAt: string | null;
  mutedUntil: string | null;
  resolvedAt: string | null;
  deliveredChannels: string[];
  triggerMetricValue: number | null;
  triggerThresholdValue: number | null;
  createdAt: string;
}

export interface IAlertDetail extends IAlertSummary {
  buyerBusinessId: string | null;
  recommendedActions: string[];
  acknowledgedByUserId: string | null;
  productReturnMetricId: string | null;
  // O149 — note saved when the alert was resolved; null before RESOLVED or when the column is not migrated.
  resolutionNotes?: string | null;
}

export interface IAlertListParams {
  status?: AlertStatus | AlertStatus[];
  severity?: AlertSeverity | AlertSeverity[];
  triggerType?: AlertTrigger | AlertTrigger[];
  productId?: string;
  page?: number;
  limit?: number;
}

export interface IAlertListResponse {
  items: IAlertSummary[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export const list = (params?: IAlertListParams) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.get("/ord/return-prevention-alerts", params as any) as Promise<IAlertListResponse>;

// §27 — the ProductReturnMetric snapshot backing the alert (projected by
// de-order-service alert/get.ts). reasonBreakdown maps reason → count.
export interface IAlertMetric {
  id: string;
  windowType: string | null;
  totalSoldQuantity: number;
  totalReturnedQuantity: number;
  returnRatePercent: number;
  previousReturnRatePercent: number | null;
  reasonBreakdown: Record<string, number>;
  topReason: string | null;
  topReasonShare: number | null;
  damagedQuantity: number;
  writeOffQuantity: number;
}

export interface IAlertGetResponse {
  alert: IAlertDetail;
  metric: IAlertMetric | null;
}

export const get = (id: string) =>
  httpRequest.get(`/ord/return-prevention-alerts/${id}`) as Promise<IAlertGetResponse>;

export const acknowledge = (id: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/return-prevention-alerts/${id}/acknowledge`, {} as any);

export const mute = (id: string, mutedUntil: Date) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/return-prevention-alerts/${id}/mute`, { mutedUntil: mutedUntil.toISOString() } as any);

// O149 — `notesSaved: false` = the notes column is not migrated yet; the decision itself was saved.
export interface IAlertResolveResponse {
  id: string;
  status: "RESOLVED";
  resolvedAt: string;
  notes: string | null;
  notesSaved: boolean;
}

export const resolve = (id: string, notes?: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  httpRequest.post(`/ord/return-prevention-alerts/${id}/resolve`, { notes: notes ?? null } as any) as Promise<IAlertResolveResponse>;
